/**
 * ATL-1445 stage 3, P8 — the APP HALF of the 409 conversion: a Firestore-first mirror the backend
 * refused because the organization is Supabase-master becomes a master write in outbox v2.
 *
 * Design: `docs/technical/write-cutover-stage3-app-seam.md` §4 "Backend 409 on a stray Firestore-first
 * write", §9 risk 2 (late Firestore-first writes from a device that was offline at the flip), §16.
 * The contract (status, code, body) is `@atlas/api-client`'s `write-master-refusal.ts`.
 *
 * B1 EMITS IT; NO ORGANIZATION TRIGGERS IT YET. Backend B1 (ATL-1445) sends `WRITE_MASTER_SUPABASE`
 * from its guard on every domain-W route, for a mirror of a Supabase-master organization only — and no
 * organization is Supabase-master (the setter refuses every enable until the stage-3 gates exist). Both
 * callers reach this module's work only after `masterRefusalOf` has matched B1's exact body,
 * synchronously, from the error alone, so for every organization today nothing here runs.
 * `__tests__/mirrorRefusalContract.b1.test.js` runs `masterRefusalOf` over the bytes B1's real stack
 * puts on the wire (its committed fixture), in every error shape (it replaced the P8 dormancy pin).
 *
 * WHY IT IS SAFE TO RE-SEND A REFUSED MIRROR AS THE MASTER. Request parity (design §7): a converted W
 * writer's request is the same in both modes, so the refused mirror IS the request the master write
 * would have sent — same route, same body, same device-minted document id (the backend's idempotency
 * key). B1 refuses it before any handler runs (the contract), so it changed nothing.
 *
 * WHAT IS CONVERTED. A 409 whose body is B1's (`parseWriteMasterSupabaseRefusal`) AND names the
 * organization the write belongs to, for a request whose route is domain W (`classifyV1Request`, the
 * registry), becomes a v2 entry with `origin: 'mirror-409-converted'` — or, for an absolute stock
 * level, a QUARANTINE entry outside the drain queue (`outboxQuarantine.ts`, review 1 S-3), never
 * replayed. Any other 409 is not B1's and keeps today's handling exactly.
 *
 * B1's REFUSAL IS NEVER DROPPED (review 1, S-2). B1 refuses only W mirrors of a Supabase-master
 * organization, so its refusal is itself evidence. When the app cannot convert it — the app classifies
 * the route as not W or cannot read it, the backend named another organization, v2 did not take the
 * copy — the v1 entry is HELD (no attempt consumed, its document's later entries wait) and paged ONCE
 * per entry and reason through the persisted alert ledger (`outbox_v1_mirror_conversion_failed`, Sentry
 * `['outbox-v1-conversion-failed', label, why]`; review 2, N-b), never dropped as a permanent refusal.
 * It remains under v1's 7-day bound.
 *
 * ONE PLACE CONVERTS: THE V1 DRAIN. Order is the property at stake. A refused mirror is an older or a
 * newer write of a document that may have other writes queued in v1 — or one IN FLIGHT in the v1 drain
 * at that very moment (a replayed create while the cashier deletes the same sale live). If the live path
 * appended to v2 itself, the two 409s would reach v2 in the order their RESPONSES returned, and a delete
 * could land ahead of its own create: the sale resurrected in the master. So:
 *  - REPLAY (`convertRefusedV1Entry`, called by the v1 drain, which holds the v1 drain lock and drains a
 *    document's entries in order): the v2 entry takes the v1 entry's id, so a crash between the copy
 *    and the v1 removal re-converts into the SAME entry (a duplicate id) instead of a second one. The
 *    drain removes its entry only after this resolves `converted`; on `held` the entry stays in v1,
 *    holds without consuming an attempt, and its document's later entries wait behind it — never
 *    dropped by the conversion.
 *  - LIVE (`convertRefusedLiveMirror`, from `bestEffortDualWrite`): the refused request is queued in v1
 *    (`enqueueRefusedMirror`: due at once, behind its document's entries, under v1's supersede rules —
 *    exactly where a transiently failed live write goes) and a v1 drain is kicked; that drain re-sends
 *    it, meets the same 409 and converts it under the v1 entry's id, in document order. The live path
 *    itself never writes v2.
 *
 * AFTER A CONVERSION (`notifyMirrorsConverted`, once per drain pass). The backend has just said what
 * this device has not observed: the organization is Supabase-master. Through P5's own path (no new
 * mechanism): the session's v2 drainer is started (or kicked) so the converted writes are delivered,
 * and the watcher re-reads the flag (`reevaluateWritePlane`), which reloads the session into its new
 * write mode once the flag is server-confirmed (§4/§14). Both bound in `writeCutoverSession.ts`.
 */
import {
  parseWriteMasterSupabaseRefusal,
  type ReplayableRequestConfig,
  type WriteMasterSupabaseRefusal,
} from '@atlas/api-client'
import * as Sentry from '@sentry/react-native'

import {
  deriveDocumentId,
  drainDualWriteOutbox,
  enqueueRefusedMirror,
  type OutboxEntry as V1OutboxEntry,
} from '../Firebase/dualWriteOutbox'
import { captureOperationalEvent } from '../analytics/operationalTelemetry'
import { createAsyncStorageAlertLedger, type OutboxAlertLedger } from './outboxAlerts'
import { openAppQuarantineStore, quarantinedAlert, reportQuarantined, type QuarantinedAlert } from './outboxQuarantine'
import type { OutboxStore } from './outboxStore'
import { appendV1Derived, countOrderAfterLive } from './v1Migration'
import { classifyV1Request, v2EntryFromV1Entry, type V1Request } from './v1ToV2'

export type NotApplicableReason =
  | 'not-a-refusal'
  | 'not-w'
  | 'unclassifiable'
  | 'other-organization'
  | 'no-request'
  | 'no-document-identity'
  | 'no-organization'

/** Telemetry, storage and the v1 hand-off; injectable for tests. */
export interface ConversionDeps {
  openStore: () => OutboxStore
  /** Where absolute stock levels go (review 1, S-3): never drained. */
  openQuarantineStore: () => OutboxStore
  /** Page: an absolute stock level was quarantined. */
  quarantined: (alert: QuarantinedAlert) => void
  report: (event: ConversionEvent) => void
  /** Persisted page dedupe for held refusals (review 2, N-b): one page per entry and reason. */
  ledger: OutboxAlertLedger
  /** LIVE path: queue the refused request in v1, due at once (default: v1's `enqueueRefusedMirror`). */
  queueInV1: (
    label: string,
    request: ReplayableRequestConfig,
    context: { firestoreId: string; orgFirestoreId: string },
  ) => Promise<boolean>
  /** LIVE path: run the v1 drain that converts it (default: an unthrottled `master-refusal` drain). */
  kickV1Drain: () => void
  now?: () => number
}

/**
 * Why a refused entry stays in v1, HELD and paged (review 1, S-2: B1's refusal is itself evidence that
 * the backend takes this for a W mirror of a Supabase-master organization, so it is never dropped):
 *  - `not-w` / `unclassifiable`: the app's W list and the backend disagree about this route;
 *  - `other-organization`: the backend resolved another organization than the entry's;
 *  - `no-request`: the entry carries nothing to send;
 *  - the storage outcomes: v2 (or the quarantine) did not take the copy.
 */
export type ConversionFailure =
  | 'not-w'
  | 'unclassifiable'
  | 'other-organization'
  | 'no-request'
  | 'not-convertible'
  | 'corrupt-duplicate'
  | 'indeterminate'
  | 'rejected'

export type ConversionEvent =
  | { type: 'converted'; label: string; quarantined: boolean; already: boolean; orderAfterLive: boolean | null }
  | { type: 'not-applicable'; path: 'live'; label: string; why: NotApplicableReason }
  | { type: 'queued-for-conversion'; label: string; queued: boolean }
  | { type: 'failed'; label: string; why: ConversionFailure; detail: string; entryId: string; documentId: string }

/** B1's refusal carried by `error`, or null (every response today's backend sends). Pure. */
export function masterRefusalOf(error: unknown): WriteMasterSupabaseRefusal | null {
  return parseWriteMasterSupabaseRefusal(error)
}

/** Why a refused request cannot be converted, or null when it can (before building the entry). */
function notApplicable(
  refusal: WriteMasterSupabaseRefusal | null,
  request: V1Request | undefined,
  orgFirestoreId: string | undefined,
): NotApplicableReason | null {
  if (refusal === null) return 'not-a-refusal'
  if (!request || typeof request.method !== 'string' || typeof request.url !== 'string') return 'no-request'
  if (!orgFirestoreId) return 'no-organization'
  const w = classifyV1Request(request)
  if (w === 'unclassifiable') return 'unclassifiable'
  if (w === 'not-w') return 'not-w'
  if (refusal.organizationFirestoreId !== orgFirestoreId) return 'other-organization'
  return null
}

const messageOf = (error: unknown): string => (error instanceof Error ? error.message : String(error))

/**
 * REPLAY path: the v1 drain got B1's refusal for `entry`. `converted` → v2 (or, for an absolute stock
 * level, the quarantine) holds it: remove it from v1. `held` → keep it in v1, consume no attempt, page
 * (review 1, S-2: never `not-applicable`, never dropped). Never throws.
 */
export async function convertRefusedV1Entry(
  entry: V1OutboxEntry,
  error: unknown,
  deps: ConversionDeps = productionConversionDeps(),
): Promise<'converted' | 'held'> {
  // Review 2, N-b: a held entry is re-sent after every backoff (≈ 38 times over v1's 7 days), so it
  // PAGES once per entry and reason, through the persisted alert ledger as outbox v2 does; every later
  // hold is a breadcrumb. A ledger that cannot answer never silences a page.
  const failed = async (why: ConversionFailure, detail: string, entryId: string): Promise<'held'> => {
    try {
      const key = `v1-conversion-held:${entry.orgFirestoreId}:${entry.id}:${why}`
      let seen = false
      try {
        seen = await deps.ledger.seen(key)
      } catch {
        seen = false
      }
      if (seen) {
        Sentry.addBreadcrumb({ category: 'outbox-v2', level: 'warning', message: `409 still not converted (${why})`, data: { label: entry.label, entryId } })
      } else {
        deps.report({ type: 'failed', label: entry.label, why, detail, entryId, documentId: entry.firestoreId })
        try {
          await deps.ledger.remember(key)
        } catch {
          // It may page again later; that is the safe direction.
        }
      }
    } catch {
      // Reporting never changes the outcome: the entry stays in v1.
    }
    return 'held'
  }
  try {
    const refusal = masterRefusalOf(error)
    const why = notApplicable(refusal, entry.request, entry.orgFirestoreId)
    if (why !== null) {
      const detail =
        why === 'other-organization' && refusal
          ? `the backend refused it for organization ${refusal.organizationFirestoreId}, the entry belongs to ${entry.orgFirestoreId}`
          : `B1 refused it as a W mirror; the app classifies it ${why}`
      // `not-a-refusal` / `no-organization` / `no-document-identity` cannot reach here from the drain
      // (it calls only with B1's body, and every v1 entry has an organization and a document id); they
      // are held all the same rather than given a path that drops.
      return failed(why === 'not-w' || why === 'unclassifiable' || why === 'other-organization' ? why : 'no-request', detail, entry.id)
    }
    const now = deps.now ?? Date.now
    const built = v2EntryFromV1Entry(entry, 'mirror-409-converted', now())
    if (!built.ok) return failed('not-convertible', built.reason, entry.id)
    // Review 1, S-3: an absolute stock level goes to the quarantine, outside the drain queue.
    const store = built.heldAbsolute ? deps.openQuarantineStore() : deps.openStore()
    const appended = await appendV1Derived(store, built.entry)
    if (appended.status === 'queued' || appended.status === 'already-queued') {
      if (built.heldAbsolute) {
        try {
          deps.quarantined(quarantinedAlert(built.entry, now()))
        } catch {
          // A page that fails never undoes a conversion.
        }
      }
      // Review 1, S-4: a converted stray older than a live entry already queued is an inversion; count it.
      const inverted = built.heldAbsolute ? 0 : await countOrderAfterLive(store, entry.orgFirestoreId, new Set([built.entry.entryId]))
      deps.report({
        type: 'converted',
        label: entry.label,
        quarantined: built.heldAbsolute,
        already: appended.status === 'already-queued',
        orderAfterLive: inverted === null ? null : inverted > 0,
      })
      return 'converted'
    }
    // Not durable (refused, an unreadable record holds the id, or maybe-queued): keep the v1 entry. A
    // maybe-queued copy is safe to retry later: the retry uses the SAME id, so it lands once (P2
    // obligation 4); an unreadable record is for an operator (obligation 5).
    const detail = appended.status === 'corrupt-duplicate' ? 'the entry id is held by an unreadable record' : messageOf(appended.error)
    return failed(appended.status, detail, built.entry.entryId)
  } catch (thrown) {
    return failed('rejected', messageOf(thrown), entry.id)
  }
}

/**
 * LIVE path: `bestEffortDualWrite`'s mirror got B1's refusal. `queued` → it is durable in v1, due at
 * once, and a v1 drain was kicked: that drain converts it, or holds and pages it (S-2) — the drain is
 * the one place that decides, so document order holds (module docblock). `not-applicable` → the request
 * cannot be queued at all (no request, no organization, no document identity: v1 rule 1) and today's
 * report runs, loudly. `unresolved` → v1 would not take it (today's report follows, loudly). Never throws.
 */
export async function convertRefusedLiveMirror(
  label: string,
  error: unknown,
  context: { orgFirestoreId?: string; firestoreId?: string },
  deps: ConversionDeps = productionConversionDeps(),
): Promise<'queued' | 'unresolved' | 'not-applicable'> {
  try {
    // The request exactly as it reached the transport (api-client attaches it to every write error).
    const request = (error as { requestConfig?: ReplayableRequestConfig } | null)?.requestConfig
    const org = context.orgFirestoreId
    let why: NotApplicableReason | null = null
    if (masterRefusalOf(error) === null) why = 'not-a-refusal'
    else if (!request || typeof request.method !== 'string' || typeof request.url !== 'string') why = 'no-request'
    else if (!org) why = 'no-organization'
    // v1 rule 1: no document identity, no replay — a write without its idempotency key could double-apply.
    const documentId = why === null && request ? deriveDocumentId(request, context.firestoreId) : undefined
    if (why === null && !documentId) why = 'no-document-identity'
    if (why !== null || !request || !org || !documentId) {
      deps.report({ type: 'not-applicable', path: 'live', label, why: why ?? 'no-request' })
      return 'not-applicable'
    }
    const queued = await deps.queueInV1(label, request, { firestoreId: documentId, orgFirestoreId: org })
    deps.report({ type: 'queued-for-conversion', label, queued })
    if (!queued) return 'unresolved'
    deps.kickV1Drain()
    return 'queued'
  } catch {
    return 'unresolved'
  }
}

// ============================================================================
// After a conversion: deliver, and re-read the flag (P5's path)
// ============================================================================

/**
 * Start (or kick) the session's v2 drainer for this organization and have the watcher re-read the write
 * flag. Lazily bound to `writeCutoverSession.ts` (which owns the session's drainer and the watcher hook)
 * so this module stays out of AppStack's import graph until a refusal is seen. Never throws.
 */
export function notifyMirrorsConverted(orgFirestoreId: string): void {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { onMirrorsConvertedToMaster } = require('./writeCutoverSession') as {
      onMirrorsConvertedToMaster: (orgFirestoreId: string) => void
    }
    onMirrorsConvertedToMaster(orgFirestoreId)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[outboxV2] after a 409 conversion, the drainer / flag re-read could not be started', { orgFirestoreId, error })
  }
}

// ============================================================================
// Production wiring
// ============================================================================

function openAppStore(): OutboxStore {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { openOutboxStore } = require('./store') as { openOutboxStore: () => OutboxStore }
  return openOutboxStore()
}

const splitLabel = (label: string): { module: string; op: string } => {
  const i = label.indexOf('.')
  return i === -1 ? { module: label, op: '' } : { module: label.slice(0, i), op: label.slice(i + 1) }
}

/** Counts and kinds to PostHog (`operationalTelemetry`'s rule); ids only to the console and Sentry context. */
export function reportConversion(event: ConversionEvent): void {
  const { module, op } = splitLabel(event.label)
  switch (event.type) {
    case 'converted':
      // eslint-disable-next-line no-console
      console.log(`[outboxV2] ${event.label} refused as a mirror (409 WRITE_MASTER_SUPABASE) — converted into a master write`, JSON.stringify(event))
      captureOperationalEvent('outbox_v1_mirror_converted', {
        label: event.label,
        module,
        op,
        quarantined: event.quarantined,
        already: event.already,
        order_after_live: event.orderAfterLive,
      })
      return
    case 'queued-for-conversion':
      // eslint-disable-next-line no-console
      console.log(`[outboxV2] live ${event.label} refused as a mirror — ${event.queued ? 'queued in v1 for conversion' : 'NOT queued'}`, JSON.stringify(event))
      captureOperationalEvent('outbox_v1_mirror_refused_live', { label: event.label, module, op, queued: event.queued })
      return
    case 'not-applicable':
      // Not converted: today's handling reports the refusal itself. A breadcrumb says why.
      Sentry.addBreadcrumb({ category: 'outbox-v2', level: 'warning', message: `409 not converted (${event.why})`, data: { ...event } })
      return
    case 'failed':
      // eslint-disable-next-line no-console
      console.error(`[outboxV2] 409 NOT CONVERTED ${event.label} (${event.why}) — held in the v1 outbox, a person must look`, JSON.stringify(event))
      Sentry.captureException(new Error(`outbox v2: 409 conversion of ${event.label} failed (${event.why})`), {
        level: 'error',
        fingerprint: ['outbox-v1-conversion-failed', event.label, event.why],
        tags: { outbox_v1_conversion_failed: event.why, dual_write: event.label, dual_write_module: module, dual_write_op: op },
        contexts: { outbox_v2: { ...event } },
      })
      captureOperationalEvent('outbox_v1_mirror_conversion_failed', { label: event.label, module, op, why: event.why })
  }
}

export function productionConversionDeps(): ConversionDeps {
  return {
    openStore: openAppStore,
    openQuarantineStore: openAppQuarantineStore,
    quarantined: reportQuarantined,
    report: reportConversion,
    ledger: createAsyncStorageAlertLedger(),
    queueInV1: (label, request, context) => enqueueRefusedMirror(label, request, context),
    // Twice, chained: if a drain is already in flight the first call only joins it (v1 coalesces), and
    // that pass took its snapshot before this entry was queued; the second is a fresh pass that sees it.
    kickV1Drain: () => {
      void drainDualWriteOutbox('master-refusal')
        .catch(() => undefined)
        .then(() => drainDualWriteOutbox('master-refusal'))
        .catch(() => undefined)
    },
  }
}
